import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  ApiResponseEnvelope,
  Clinic,
  PaginatedListResult,
  PaginatedQueryOptions,
} from '@doctor-scheduling/shared/models';
import { API_BASE_URL } from './api-base-url.token';
import { buildListHttpParams } from './list-query.util';
import { DoctorListItem } from './doctor.api';

/** Clinic row with optional doctor count from detail endpoint. */
export interface ClinicListItem extends Clinic {
  doctorCount?: number;
}

/** Payload for creating a clinic. */
export interface CreateClinicPayload {
  name: string;
  slug?: string;
  address: string;
  city: string;
  postcode: string;
  country: string;
  phone?: string;
  email?: string;
  logo?: string;
}

/** Partial clinic update payload. */
export type UpdateClinicPayload = Partial<CreateClinicPayload> & {
  isActive?: boolean;
};

/** HTTP client for platform clinic CRUD. */
@Injectable({
  providedIn: 'root',
})
export class ClinicApi {
  /** Angular HTTP client used for API requests. */
  private readonly http = inject(HttpClient);
  /** NestJS API origin from the web app environment. */
  private readonly apiBaseUrl = inject(API_BASE_URL);
  /** Base URL for clinic routes under `/api/v1/clinics`. */
  private readonly baseUrl = `${this.apiBaseUrl}/api/v1/clinics`;

  /**
   * Lists clinics with Eaze-style page/sort/filter query params.
   * @param opts - Pagination, sort, search, and filter maps
   */
  findAll(
    opts?: PaginatedQueryOptions
  ): Observable<ApiResponseEnvelope<PaginatedListResult<ClinicListItem>>> {
    return this.http.get<ApiResponseEnvelope<PaginatedListResult<ClinicListItem>>>(
      this.baseUrl,
      { params: buildListHttpParams(opts) }
    );
  }

  /**
   * Loads a single clinic by id (includes doctorCount).
   * @param id - Clinic UUID
   */
  findOne(id: string): Observable<ApiResponseEnvelope<ClinicListItem>> {
    return this.http.get<ApiResponseEnvelope<ClinicListItem>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Lists doctors belonging to a clinic.
   * @param clinicId - Clinic UUID
   * @param opts - List query options
   */
  findDoctors(
    clinicId: string,
    opts?: PaginatedQueryOptions
  ): Observable<ApiResponseEnvelope<PaginatedListResult<DoctorListItem>>> {
    return this.http.get<ApiResponseEnvelope<PaginatedListResult<DoctorListItem>>>(
      `${this.baseUrl}/${clinicId}/doctors`,
      { params: buildListHttpParams(opts) }
    );
  }

  /**
   * Creates a clinic tenant.
   * @param data - Clinic fields
   */
  create(data: CreateClinicPayload): Observable<ApiResponseEnvelope<ClinicListItem>> {
    return this.http.post<ApiResponseEnvelope<ClinicListItem>>(this.baseUrl, data);
  }

  /**
   * Updates a clinic profile.
   * @param id - Clinic UUID
   * @param data - Partial fields
   */
  update(
    id: string,
    data: UpdateClinicPayload
  ): Observable<ApiResponseEnvelope<ClinicListItem>> {
    return this.http.put<ApiResponseEnvelope<ClinicListItem>>(
      `${this.baseUrl}/${id}`,
      data
    );
  }

  /**
   * Soft-deletes a clinic.
   * @param id - Clinic UUID
   */
  delete(id: string): Observable<ApiResponseEnvelope<ClinicListItem>> {
    return this.http.delete<ApiResponseEnvelope<ClinicListItem>>(
      `${this.baseUrl}/${id}`
    );
  }
}
